import { useState } from 'react'
import { Alert, Button, Checkbox, Input, Radio } from 'antd'
import { MailOutlined, SendOutlined } from '@ant-design/icons'
import {
  REPLY_STATUSES,
  SUPPORT_REPLY_MAX,
  isReplyStatus,
  startsWithFormulaCharacter,
  type ReplyStatus,
} from '@/api/advisorySupport'
import { TICKET_STATUS_ADMIN_LABEL } from '@/features/advisory-ai/assistant/mockData'

/** Reads after "Send" or "Log" — the latter because a reply already sent by email is only recorded. */
const ACTION_SUFFIX: Record<ReplyStatus, string> = {
  awaiting: ' and wait for reply',
  reviewing: ', keep reviewing',
  closed: ' and close',
}

type TicketReplyComposerProps = {
  customerName: string
  value: string
  onChange: (value: string) => void
  /** Owned by the page, so a failure is still shown after the drawer is closed and reopened. */
  error: string
  /**
   * Also owned by the page, and cleared there once a reply has gone: left to
   * this component it would survive a successful send, and the next reply on the
   * same ticket would suppress the customer's notice email without saying so.
   */
  sentByEmail: boolean
  onSentByEmailChange: (next: boolean) => void
  sending: boolean
  onSend: (status: ReplyStatus) => void
}

export function TicketReplyComposer({
  customerName,
  value,
  onChange,
  error,
  sentByEmail,
  onSentByEmailChange,
  sending,
  onSend,
}: TicketReplyComposerProps) {
  const [status, setStatus] = useState<ReplyStatus>('awaiting')

  const invalidStart = startsWithFormulaCharacter(value)
  const canSend = Boolean(value.trim()) && !invalidStart && !sending

  return (
    <div className="space-y-2">
      <Input.TextArea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={
          sentByEmail
            ? 'Paste the reply you already sent…'
            : 'Hi {first name},\n\n…\n\n{your name}'
        }
        maxLength={SUPPORT_REPLY_MAX}
        showCount={value.length > SUPPORT_REPLY_MAX * 0.8}
        autoSize={{ minRows: 6, maxRows: 16 }}
        disabled={sending}
        aria-label="Reply to the customer"
      />

      {invalidStart && (
        <div className="text-[11.5px] text-red-500">
          A reply can&rsquo;t start with =, +, - or @. Start with the greeting.
        </div>
      )}

      <Checkbox
        checked={sentByEmail}
        onChange={(event) => onSentByEmailChange(event.target.checked)}
        disabled={sending}
        aria-label="I already replied to this by email"
      >
        <span className="text-[12px]">
          <MailOutlined className="mr-1 text-gray-400" />
          I already replied to this by email
        </span>
      </Checkbox>

      <div className="text-[11.5px] leading-relaxed text-gray-500">
        {sentByEmail ? (
          <>
            Recorded on the thread and shown to {customerName || 'the customer'} in Preppy exactly as
            written, marked as sent by email. No notice is emailed — they already have your reply.
          </>
        ) : (
          <>
            Posted to the thread and shown to {customerName || 'the customer'} in Preppy exactly as written.
            They are emailed a notice with a link — not the message itself.
          </>
        )}
      </div>

      {error && <Alert type="error" showIcon message={error} />}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Radio.Group
          size="small"
          optionType="button"
          value={status}
          onChange={(event) => {
            const next: unknown = event.target.value
            if (isReplyStatus(next)) setStatus(next)
          }}
          options={REPLY_STATUSES.map((option) => ({ value: option, label: TICKET_STATUS_ADMIN_LABEL[option] }))}
          disabled={sending}
          aria-label="Status after sending"
        />
        <Button
          type="primary"
          className="feature-button-solid"
          icon={sentByEmail ? <MailOutlined /> : <SendOutlined />}
          loading={sending}
          disabled={!canSend}
          onClick={() => canSend && onSend(status)}
        >
          {(sentByEmail ? 'Log' : 'Send') + ACTION_SUFFIX[status]}
        </Button>
      </div>
    </div>
  )
}
